import { axiosPrivate } from 'shared/api';
import { IInvoice } from 'features/inventory/invoices/types';
import { t } from 'i18next';
import { BaseService } from 'shared/services/BaseService';
import toast from 'shared/utils/toast';

class InvoiceService extends BaseService<IInvoice> {
  constructor() {
    super('invoices');
  }
  canDelete = true;
  canBulkDelete = true;

  importFromFile = async (importFile: File, attachmentName = 'import_file') => {
    const res = await axiosPrivate.postForm(`${this.modulePrefix}/import-from-file/`, {
      [attachmentName]: importFile
    });
    return res.data;
  };

  emailInvoiceAgreement = async (invoiceId: number) => {
    this.email('email-frontispiece', invoiceId);
  };

  emailCreditMemo = async (invoiceId: number) => {
    this.email('email-credit-memo', invoiceId);
  };

  async email(endpoint: string, invoiceId: number) {
    try {
      const res = await axiosPrivate.get(`${this.modulePrefix}/${invoiceId}/${endpoint}`);
      if (res.status === 200) {
        toast.info(t('toasts.email-sent-to-supplier'));
      }
      return res.data;
    } catch (e) {
      toast.error(e.message);
    }
  }

  downloadInvoiceAgreement = async (invoiceId: number) => {
    return this.download('download-invoice-frontispiece', invoiceId);
  };

  downloadCDFrontspieceAgreement = async (invoiceId: number) => {
    this.download('download-credit-debit-frontispiece', invoiceId);
  };

  downloadCreditMemo = async (invoiceId: number) => {
    this.download('download-credit-memo', invoiceId);
  };

  download = async (endpoint: string, invoiceId: number) => {
    return axiosPrivate
      .get(`${this.modulePrefix}/${invoiceId}/${endpoint}/`, {
        responseType: 'blob'
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const contentDisposition = res.headers['content-disposition'];
        if (contentDisposition?.indexOf('attachment') !== -1) {
          const filenameMatches = contentDisposition.match(
            /(?:.*filename\*|filename)=(?:([^'"]*)''|("))([^;]+)\2(?:[;`\n]|$)/
          );
          const filename = filenameMatches.pop();
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
        }
      })
      .catch(async (err) => {
        // Se il backend risponde con un errore (es. 500) e il responseType è blob,
        // l'errore si trova dentro err.response.data sotto forma di Blob.
        if (err.response?.data instanceof Blob) {
          try {
            // Convertiamo il Blob di errore in una stringa di testo JSON o HTML
            const textError = await err.response.data.text();

            // Proviamo a fare il parse se il server ha risposto in JSON
            try {
              const parsedJson = JSON.parse(textError);
              err.serverErrorDetails = parsedJson.message || parsedJson.error || textError;
            } catch {
              // Se non è JSON (es. pagina di debug HTML di Django), passiamo il testo o l'estratto
              err.serverErrorDetails = textError.substring(0, 500);
            }
          } catch (e) {
            err.serverErrorDetails = 'Impossibile leggere i dettagli dal server.';
          }
        }
        // Propaga l'errore modificato in modo che InvoiceDetail lo possa intercettare nel .catch()
        throw err;
      });
  };

  generateCreditMemo = async (invoiceId: number, credit_memo_date?: string) => {
    try {
      const res = await axiosPrivate.post(
        `${this.modulePrefix}/${invoiceId}/generate-credit-memo/`,
        { credit_memo_date }
      );
      if (res.status === 200) {
        toast.info(t('toasts.credit-memo-generated'));
      }
      return res.data;
    } catch (e) {
      toast.error(e.message);
    }
  };
}

export const invoiceService = new InvoiceService();
